import type { Entity } from '@models/Entity'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { ENTITIES } from '../../entity'
import { Activiteiten } from '../../entity/activiteiten/components/Activiteiten'
import { useSearchActiviteitenForEntity } from '../../entity/activiteiten/hooks/useSearchActiviteitenForEntity'
import { Bijlagen } from '../../entity/bijlagen/components/Bijlagen'
import { useBijlagenForEntity } from '../../entity/bijlagen/hooks/useBijlagenForEntity'
import { useHerplanGeblokkeerdeWeken } from '../../hoofdleiding/project/hooks/useHerplanGeblokkeerdeWeken'
import { useProjectmelding } from '../../projectmelding/hooks/useProjectmelding'
import { useParams } from '../../router/hooks/useParams'
import { Page } from '../../theme/components/Page'
import { PageSection } from '../../theme/components/PageSection'
import { StyledExpandCard } from '../../theme/components/StyledExpandCard'
import { useHoofdleidingValuelists } from '../../valuelists/hooks/useHoofdleidingValuelists'
import { useProjectdossierGlobalsLists } from '../../valuelists/hooks/useProjectdossierGlobalsLists'
import { DetailGrid } from '../components/DetailGrid'
import { LinkedProjectmelding } from '../components/LinkedProjectmelding'
import { LinkedProjects } from '../components/LinkedProjects'
import { useOrganisatiesByGebied } from '../hooks/useOrganisatiesByGebied'
import { useProjectdossier } from '../hooks/useProjectdossier'

export const Projectdossier = () => {
  const { id } = useParams()
  const { organisatie } = useAuthenticatedUser()

  const { data: projectdossierLists } = useProjectdossierGlobalsLists()
  const { data: hoofdleidingValuelists } = useHoofdleidingValuelists()
  const { data: dossier } = useProjectdossier({ id: id || '' })
  const { data: projectmelding } = useProjectmelding({
    id: dossier?.projectmeldingId,
    projectdossierId: dossier?.projectdossierId,
  })
  const { data: organisaties } = useOrganisatiesByGebied({
    gebiedscode: dossier?.gebiedscode || '',
  })

  const entity = {
    id,
    type: ENTITIES.PROJECTDOSSIER,
    gebiedscode: dossier?.gebiedscode || '',
  } as Entity

  const { data: activiteiten } = useSearchActiviteitenForEntity(entity)
  const { data: bijlagen } = useBijlagenForEntity(entity)
  const {
    data: { items: herplanGeblokkeerdeWeken },
  } = useHerplanGeblokkeerdeWeken({
    gebiedscodes: [dossier?.gebiedscode || ''],
    organisatie,
  })

  return (
    <Page
      title={[
        dossier ? dossier.projectdossierId : 'Projectdossier',
        'Ketensamenwerking',
      ]}
      breadcrumbs={[
        { label: 'Ketensamenwerking', href: '/home', tooltip: 'CAPO home' },
        {
          label: 'Projectdossiers',
          href: '/projectdossiers',
          tooltip: 'Projectdossiers overzicht',
        },
        {
          label: dossier ? dossier.projectdossierId : 'Projectdossier',
        },
      ]}
    >
      <PageSection
        id="projectdossier"
        heading={
          !dossier?.projectdossierId
            ? 'Projectdossier niet gevonden'
            : `Projectdossier ${dossier.projectdossierId}`
        }
      />

      {dossier?.projectdossierId && (
        <>
          <DetailGrid
            dossier={dossier}
            organisaties={organisaties}
            statussen={projectdossierLists.status}
          />

          {projectmelding && (
            <LinkedProjectmelding projectmelding={projectmelding} />
          )}

          <StyledExpandCard title="Activiteiten">
            <Activiteiten
              activiteiten={activiteiten}
              entity={entity}
              assignees={organisaties}
              geblokkeerdeWeken={herplanGeblokkeerdeWeken}
            />
          </StyledExpandCard>

          <Bijlagen bijlagen={bijlagen} entity={entity} />

          <LinkedProjects
            entity={entity}
            linkedProjectIds={dossier.projects}
            projectmelding={projectmelding}
            projectdossier={dossier}
            geblokkeerdeWeken={herplanGeblokkeerdeWeken}
            hoofdleidingGebieden={hoofdleidingValuelists.gebieden}
          />
        </>
      )}
    </Page>
  )
}
